import React, { useState, useEffect } from 'react';
import { Progress, Button, Typography, App } from 'antd';
import { DownloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import MediumModal from '../Components/Modals/MediumModal';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../data/base';

const { Text } = Typography;

/**
 * ========================================
 * COMPONENTE DOWNLOAD VARIANTS CSV MODAL
 * ========================================
 * 
 * SCOPO DEL COMPONENTE:
 * Questo componente permette agli amministratori di scaricare un file CSV contenente
 * tutte le varianti di occhiali che hanno status "Pubblicato" nel database Firebase.
 * 
 * ========================================
 * LOGICA DI FUNZIONAMENTO DETTAGLIATA:
 * ========================================
 * 
 * 📊 COSA SCARICA:
 * ----------------
 * Il componente genera un file CSV con le seguenti 4 colonne:
 * 1. SKU Code     → Campo "skuModel" di ogni variante (es: "206729LHF55SP")
 * 2. EAN Code     → Campo "eanCode" di ogni variante (es: "0716736995687") 
 * 3. UPC Code     → Campo "upcCode" di ogni variante (es: "716736995687")
 * 4. Glasses Name → Campo "glassesName" di ogni variante (es: "PLD 4161/S")
 * 
 * 🗃️ DA DOVE SCARICA:
 * -------------------
 * DATABASE: Firebase Firestore
 * COLLEZIONE: "Variants"
 * FILTRO: Documenti con campo "status" = "Pubblicato"
 * 
 * Query Firebase utilizzata:
 * ```javascript
 * const variantsQuery = query(
 *     collection(db, "Variants"),
 *     where("status", "==", "Pubblicato")
 * );
 * ```
 * 
 * 📋 ESEMPIO DI DOCUMENTO FIRESTORE PROCESSATO:
 * ---------------------------------------------
 * Documento in Firestore:
 * {
 *   id: "CRsy2X1xNp95dFJLY7ll",
 *   skuModel: "206729LHF55SP",
 *   eanCode: "0716736995687", 
 *   upcCode: "716736995687",
 *   glassesName: "PLD 4161/S",
 *   status: "Pubblicato",
 *   frameColor: "BURGUNDY",
 *   lensesColor: "GOLD",
 *   // ... altri campi ignorati
 * }
 * 
 * Riga nel CSV risultante:
 * "206729LHF55SP","0716736995687","716736995687","PLD 4161/S"
 * 
 * ========================================
 * DIAGRAMMA DI FLUSSO:
 * ========================================
 * 
 *     👤 UTENTE ADMIN
 *         |
 *         | Click "Download CSV"
 *         ▼
 *    🪟 MODAL CONFERMA
 *         |
 *         | Click "Download" 
 *         ▼
 *    🔥 FIREBASE QUERY
 *    📊 Collection: "Variants"
 *    🔍 WHERE status = "Pubblicato"
 *         |
 *         | Carica documenti
 *         ▼
 *    📋 ESTRAZIONE DATI
 *    • skuModel → SKU Code
 *    • eanCode → EAN Code  
 *    • upcCode → UPC Code
 *    • glassesName → Glasses Name
 *         |
 *         | Trasforma in CSV
 *         ▼
 *    📄 GENERAZIONE CSV
 *    Header + Righe escaped
 *         |
 *         | Crea Blob + URL
 *         ▼
 *    💾 DOWNLOAD AUTOMATICO
 *    File: "published_variants_export.csv"
 *         |
 *         ▼
 *    ✅ SUCCESSO + OPTION MANUAL DOWNLOAD
 * 
 * ========================================
 * FLUSSO DI ESECUZIONE DETTAGLIATO:
 * ========================================
 * 
 * FASE 1: CONFERMA (Modal iniziale)
 * ----------------------------------
 * - Mostra all'utente cosa verrà scaricato
 * - Elenca le 4 colonne che saranno nel CSV
 * - Spiega che verranno incluse solo le varianti "Published"
 * - Bottoni: [Cancel] [Download]
 * 
 * FASE 2: CARICAMENTO DATI (Progresso 1-50%)
 * -------------------------------------------
 * Progressione graduale con delays realistici:
 * 
 * 1% → "Initializing connection..." (200ms delay)
 * 3% → "Connecting to database..." (300ms delay)  
 * 5% → "Preparing query..." (200ms delay)
 * 8% → "Executing query..." (400ms delay)
 * 15% → "Loading published variants..." (esecuzione query Firebase)
 * 25% → "Query completed, processing data..." (300ms delay)
 * 30-45% → "Processed X/Y variants..." (progresso ogni 100 documenti)
 * 47% → "Finalizing data processing..." (300ms delay)
 * 50% → "Loaded X published variants successfully"
 * 
 * Query Firebase eseguita:
 * collection(db, "Variants").where("status", "==", "Pubblicato")
 * 
 * FASE 3: GENERAZIONE CSV (Progresso 50-100%)
 * --------------------------------------------
 * Progressione con elaborazione realistica:
 * 
 * 52% → "Preparing CSV generation..." (200ms delay)
 * 55% → "Creating CSV header..." (300ms delay)
 * 58% → "Starting to process X variants..." (200ms delay)
 * 58-95% → "Processing variant X/Y..." (con delays periodici)
 *          • Delay 10ms ogni 50 varianti
 *          • Delay 50ms ogni 200 varianti
 * 97% → "Finalizing CSV content..." (400ms delay)
 * 100% → "CSV generation completed!"
 * 
 * Header CSV: "SKU Code,EAN Code,UPC Code,Glasses Name"
 * Escape automatico di virgole, virgolette e caratteri speciali
 * 
 * FASE 4: DOWNLOAD (Completamento)
 * ---------------------------------
 * - Creazione Blob con il contenuto CSV
 * - Generazione URL temporaneo per il download  
 * - Download automatico del file "published_variants_export.csv"
 * - Mostra interfaccia di successo con possibilità di ri-download manuale
 * - Cleanup automatico delle URL temporanee
 * 
 * ========================================
 * GESTIONE ERRORI:
 * ========================================
 * 
 * - Errori di connessione Firebase → Messaggio "Error generating CSV file"
 * - Problemi durante la generazione → Reset dello stato e messaggio di errore
 * - Gestione cleanup delle URL temporanee per evitare memory leak
 * 
 * ========================================
 * MIGLIORAMENTI UX IMPLEMENTATI:
 * ========================================
 * 
 * PROGRESSO REALISTICO:
 * - Barra di progresso inizia da 1% invece di saltare al 10%
 * - Progressione graduale con 15+ steps intermedi
 * - Delays realistici per simulare operazioni di database/processing
 * - Messaggi dettagliati per ogni fase dell'operazione
 * 
 * PERFORMANCE OTTIMIZZATA:
 * - Delays periodici per evitare blocco dell'UI (10ms ogni 50 items)
 * - Aggiornamenti progressivi ogni 100 documenti processati
 * - Conversione da forEach a for-loop per gestire async/await
 * - Cleanup automatico delle risorse per evitare memory leaks
 * 
 * ========================================
 * SICUREZZA E PERFORMANCE:
 * ========================================
 * 
 * - Accesso limitato solo ad utenti con ruolo Admin
 * - Query ottimizzata con WHERE clause per ridurre i dati trasferiti
 * - Escape dei valori CSV per prevenire injection
 * - Cleanup automatico delle URL temporanee
 * - Progress tracking granulare per migliorare UX su grandi dataset
 */

const DownloadVariantsCSVModal = ({
    visible,
    onCancel
}) => {
    const { message } = App.useApp();
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [processedVariants, setProcessedVariants] = useState(0);
    const [totalVariants, setTotalVariants] = useState(0);
    const [currentPhase, setCurrentPhase] = useState(''); // 'loading' o 'generating'

    useEffect(() => {
        if (visible) {
            // If we have a downloadUrl from previous generation, keep the complete state
            if (downloadUrl) {
                setIsGenerating(false);
                setProgress(100);
                setIsComplete(true);
                setCurrentPhase('');
            } else {
                // Reset states when modal opens fresh
                setIsGenerating(false);
                setProgress(0);
                setIsComplete(false);
                setProcessedVariants(0);
                setTotalVariants(0);
                setCurrentPhase('');
            }
        } else {
            // Only revoke URL when modal closes
            if (downloadUrl) {
                URL.revokeObjectURL(downloadUrl);
                setDownloadUrl(null);
            }
        }
    }, [visible, downloadUrl]);

    // Separate useEffect to handle cleanup when component unmounts
    useEffect(() => {
        return () => {
            if (downloadUrl) {
                URL.revokeObjectURL(downloadUrl);
            }
        };
    }, [downloadUrl]);

    /**
     * FUNZIONE CARICAMENTO VARIANTI DA FIREBASE
     * ==========================================
     * 
     * COSA FA:
     * Carica dal database Firebase tutte le varianti che hanno status "Pubblicato"
     * ed estrae solo i 4 campi necessari per il CSV.
     * 
     * DA DOVE CARICA:
     * - Database: Firebase Firestore
     * - Collezione: "Variants" 
     * - Filtro: status = "Pubblicato"
     * 
     * CAMPI ESTRATTI:
     * - skuModel: Codice SKU (es: "206729LHF55SP")
     * - eanCode: Codice EAN (es: "0716736995687")  
     * - upcCode: Codice UPC (es: "716736995687")
     * - glassesName: Nome occhiali (es: "PLD 4161/S")
     * 
          * PROGRESSO DETTAGLIATO:
      * - 1%: Inizializzazione connessione  
      * - 3%: Connessione database
      * - 5%: Preparazione query
      * - 8%: Esecuzione query
      * - 15%: Caricamento varianti
      * - 25%: Completamento query
      * - 30-45%: Elaborazione documenti (progressiva)
      * - 47%: Finalizzazione elaborazione
      * - 50%: Completamento caricamento
     */
    const loadAllPublishedVariants = async (onProgress) => {
        try {
            // STEP 1: Inizializzazione graduale per UX migliore
            onProgress(1, 'Initializing connection...');
            await new Promise(resolve => setTimeout(resolve, 200)); // Delay per UX

            onProgress(3, 'Connecting to database...');
            await new Promise(resolve => setTimeout(resolve, 300));

            onProgress(5, 'Preparing query...');
            await new Promise(resolve => setTimeout(resolve, 200));

            // STEP 2: Creazione query per Firebase Firestore
            // Seleziona collezione "Variants" e filtra per status "Pubblicato"
            const variantsRef = collection(db, "Variants");
            const variantsQuery = query(
                variantsRef,
                where("status", "==", "Pubblicato") // FILTRO CRUCIALE: solo varianti pubblicate
            );

            onProgress(8, 'Executing query...');
            await new Promise(resolve => setTimeout(resolve, 400));

            // STEP 3: Esecuzione query e recupero documenti
            onProgress(15, 'Loading published variants...');
            const querySnapshot = await getDocs(variantsQuery);

            onProgress(25, 'Query completed, processing data...');
            await new Promise(resolve => setTimeout(resolve, 300));

            const variantsData = [];
            const totalDocs = querySnapshot.docs.length;

            // STEP 4: Elaborazione di ogni documento trovato con progresso graduale
            onProgress(30, `Processing ${totalDocs} variants...`);

            let processedDocs = 0;
            querySnapshot.forEach((doc) => {
                const data = doc.data(); // Dati del documento Firestore

                // ESTRAZIONE SOLO DEI 4 CAMPI NECESSARI
                // Ignoriamo tutti gli altri campi come frameColor, lensesColor, poster, ecc.
                variantsData.push({
                    id: doc.id, // ID documento per debug
                    skuModel: data.skuModel || '', // SKU Code → Prima colonna CSV
                    eanCode: data.eanCode || '',   // EAN Code → Seconda colonna CSV  
                    upcCode: data.upcCode || '',   // UPC Code → Terza colonna CSV
                    glassesName: data.glassesName || 'Unknown Glasses' // Glasses Name → Quarta colonna CSV
                });

                processedDocs++;

                // Aggiorna progresso ogni 100 documenti o alla fine
                if (processedDocs % 100 === 0 || processedDocs === totalDocs) {
                    const progressPercent = Math.round(30 + ((processedDocs / totalDocs) * 15));
                    onProgress(progressPercent, `Processed ${processedDocs}/${totalDocs} variants...`);
                }
            });

            onProgress(47, 'Finalizing data processing...');
            await new Promise(resolve => setTimeout(resolve, 300));

            onProgress(50, `Loaded ${variantsData.length} published variants successfully`);
            console.log(`📊 Caricate ${variantsData.length} varianti pubblicate da Firebase`);
            return variantsData;
        } catch (error) {
            console.error("❌ Errore durante caricamento varianti da Firebase:", error);
            throw error;
        }
    };

    /**
     * FUNZIONE GENERAZIONE CSV
     * =========================
     * 
     * COSA FA:
     * Trasforma i dati delle varianti in formato CSV con le 4 colonne richieste.
     * Gestisce correttamente l'escape dei caratteri speciali per evitare problemi.
     * 
     * INPUT:
     * Array di varianti con i campi: skuModel, eanCode, upcCode, glassesName
     * 
     * OUTPUT:
     * Stringa CSV formattata con:
     * - Header: "SKU Code,EAN Code,UPC Code,Glasses Name"
     * - Una riga per ogni variante
     * - Valori escaped per gestire virgole e virgolette
     * 
           * PROGRESSO DETTAGLIATO:
      * - 52%: Preparazione generazione CSV
      * - 55%: Creazione header CSV  
      * - 58%: Inizio elaborazione varianti
      * - 58-95%: Elaborazione varianti (con delays realistici)
      * - 97%: Finalizzazione contenuto CSV
      * - 100%: Generazione CSV completata
     * 
     * ESEMPIO OUTPUT:
     * ```
     * SKU Code,EAN Code,UPC Code,Glasses Name
     * "206729LHF55SP","0716736995687","716736995687","PLD 4161/S"
     * "207845MNO22XL","1234567890123","234567890123","RAY-BAN RX 5285"
     * ```
     */
    const generateVariantsCSV = (variants, onProgress) => {
        return new Promise(async (resolve) => {
            console.log(`📝 Inizio generazione CSV per ${variants.length} varianti`);

            // STEP 1: Inizializzazione graduale
            onProgress(52, 'Preparing CSV generation...');
            await new Promise(resolve => setTimeout(resolve, 200));

            // STEP 2: Creazione header CSV con le 4 colonne richieste
            onProgress(55, 'Creating CSV header...');
            await new Promise(resolve => setTimeout(resolve, 300));
            let csvContent = "SKU Code,EAN Code,UPC Code,Glasses Name\n";

            const total = variants.length;
            let processed = 0;

            onProgress(58, `Starting to process ${total} variants...`);
            await new Promise(resolve => setTimeout(resolve, 200));

            // STEP 3: Iterazione su ogni variante per creare le righe CSV
            for (let index = 0; index < variants.length; index++) {
                const variant = variants[index];

                /**
                 * FUNZIONE ESCAPE CSV
                 * ===================
                 * Gestisce i caratteri speciali nei valori CSV:
                 * - Virgole: wrappa in virgolette
                 * - Virgolette: raddoppia le virgolette  
                 * - A capo: wrappa in virgolette
                 * 
                 * Esempi:
                 * "Hello, World" → "\"Hello, World\""
                 * "Say \"Hi\"" → "\"Say \"\"Hi\"\"\""
                 */
                const escapeCSVValue = (value) => {
                    if (value === null || value === undefined) return '';
                    const stringValue = String(value);
                    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                };

                // STEP 4: Costruzione riga CSV con i 4 valori escaped
                // Ordine colonne: SKU Code, EAN Code, UPC Code, Glasses Name
                const row = [
                    escapeCSVValue(variant.skuModel),   // Colonna 1: SKU Code
                    escapeCSVValue(variant.eanCode),    // Colonna 2: EAN Code  
                    escapeCSVValue(variant.upcCode),    // Colonna 3: UPC Code
                    escapeCSVValue(variant.glassesName) // Colonna 4: Glasses Name
                ].join(',');

                csvContent += row + '\n';

                // STEP 5: Aggiornamento progresso (58% → 95%) con delays realistici
                processed++;
                const csvProgress = Math.round(58 + ((processed / total) * 37)); // Da 58% a 95%
                onProgress(csvProgress, `Processing variant ${processed}/${total}...`);

                // Log ogni 100 varianti per debug
                if (processed % 100 === 0) {
                    console.log(`📊 Processate ${processed}/${total} varianti (${csvProgress}%)`);
                }

                // Delay periodici per simulare elaborazione realistica
                if (processed % 50 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 10)); // Piccolo delay ogni 50 items
                }
                if (processed % 200 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 50)); // Delay più lungo ogni 200 items
                }
            }

            // STEP 6: Finalizzazione
            onProgress(97, 'Finalizing CSV content...');
            await new Promise(resolve => setTimeout(resolve, 400));

            onProgress(100, 'CSV generation completed!');
            console.log(`✅ CSV generato con successo: ${csvContent.length} caratteri, ${total} righe dati`);

            resolve(csvContent);
        });
    };

    const handleConfirmDownload = async () => {
        try {
            setIsGenerating(true);
            setProgress(0);
            setProcessedVariants(0);
            setTotalVariants(0);
            setCurrentPhase('loading');

            // FASE 1: Caricamento varianti (0-50%)
            const variants = await loadAllPublishedVariants((progress, message) => {
                setProgress(progress);
                setCurrentPhase(`loading: ${message}`);
            });

            setTotalVariants(variants.length);

            // FASE 2: Generazione CSV (50-100%)
            setCurrentPhase('generating');

            const csvData = await generateVariantsCSV(variants, (progress, message) => {
                setProgress(progress);
                setCurrentPhase(`generating: ${message}`);
                setProcessedVariants(progress === 100 ? variants.length : Math.round((progress - 50) / 50 * variants.length));
            });

            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            setDownloadUrl(url);

            const link = document.createElement('a');
            link.href = url;
            link.download = 'published_variants_export.csv';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                setIsComplete(true);
                setCurrentPhase('');
                message.success('CSV generated and downloaded successfully!');
            }, 500);

        } catch (error) {
            console.error('Error during CSV generation:', error);
            message.error('Error generating CSV file');
            setIsGenerating(false);
            setCurrentPhase('');
        }
    };

    const handleManualDownload = () => {
        if (downloadUrl) {
            try {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = 'published_variants_export.csv';
                link.style.display = 'none';
                document.body.appendChild(link);

                link.click();
                document.body.removeChild(link);

                message.success('Download started!');
            } catch (error) {
                console.error('Error during manual download:', error);
                message.error('Error during manual download');
            }
        } else {
            console.error('No download URL available');
            message.error('Download URL not available. Please try generating the file again.');
        }
    };

    const handleClose = () => {
        if (downloadUrl) {
            URL.revokeObjectURL(downloadUrl);
        }
        onCancel();
    };

    const renderContent = () => {
        // ===============================
        // SEZIONE CONFERMA
        // ===============================
        if (!isGenerating && !isComplete) {
            return (
                <div style={{ padding: '0' }}>
                    <Text style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: '#333',
                        display: 'block',
                        marginBottom: '20px'
                    }}>
                        Are you sure you want to download the CSV with all published variants?
                    </Text>

                    <Text style={{
                        fontSize: '14px',
                        color: '#666',
                        display: 'block',
                        marginBottom: '16px'
                    }}>
                        The file will contain a table with:
                    </Text>

                    <ul style={{
                        marginTop: '0',
                        marginBottom: '20px',
                        paddingLeft: '20px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#666'
                    }}>
                        <li>All variants with status "Published"</li>
                        <li><strong>SKU Code</strong> - Variant identification code</li>
                        <li><strong>EAN Code</strong> - Variant EAN code</li>
                        <li><strong>UPC Code</strong> - Variant UPC code</li>
                        <li><strong>Glasses Name</strong> - Name of the glasses</li>
                    </ul>

                    <Text style={{
                        fontSize: '13px',
                        color: '#8c8c8c',
                        fontStyle: 'italic',
                        display: 'block'
                    }}>
                        <strong>Note:</strong> The system will load all published variants before generating the CSV. This may take a few moments for databases with many variants.
                    </Text>
                </div>
            );
        }

        // ===============================
        // SEZIONE GENERAZIONE
        // ===============================
        if (isGenerating) {
            return (
                <div style={{ padding: '20px 0', textAlign: 'center' }}>
                    <Text style={{
                        fontSize: '16px',
                        color: '#333',
                        display: 'block',
                        marginBottom: '24px'
                    }}>
                        {currentPhase.startsWith('loading:')
                            ? 'Loading variants data...'
                            : currentPhase.startsWith('generating:')
                                ? 'Generating CSV file...'
                                : 'Processing...'
                        }
                    </Text>

                    <Progress
                        percent={progress}
                        status="active"
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                        style={{ marginBottom: '16px' }}
                    />

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                    }}>
                        <Text style={{ fontSize: '14px', color: '#666' }}>
                            {currentPhase.startsWith('loading:')
                                ? currentPhase.replace('loading: ', '')
                                : currentPhase.startsWith('generating:')
                                    ? currentPhase.replace('generating: ', '')
                                    : 'Please wait...'
                            }
                        </Text>
                        {currentPhase.startsWith('generating:') && totalVariants > 0 && (
                            <Text style={{ fontSize: '14px', color: '#1890ff', fontWeight: '500' }}>
                                {processedVariants} / {totalVariants}
                            </Text>
                        )}
                    </div>

                    <Text style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {currentPhase.startsWith('loading:')
                            ? 'Please wait while we load all variants data...'
                            : currentPhase.startsWith('generating:')
                                ? 'Please wait while we generate your CSV file.'
                                : 'Processing your request...'
                        }
                    </Text>
                </div>
            );
        }

        // ===============================
        // SEZIONE COMPLETAMENTO
        // ===============================
        if (isComplete) {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <CheckCircleOutlined
                        style={{
                            fontSize: '48px',
                            color: '#52c41a',
                            marginBottom: '20px'
                        }}
                    />
                    <Text style={{
                        fontSize: '18px',
                        color: '#52c41a',
                        fontWeight: '500',
                        display: 'block',
                        marginBottom: '16px'
                    }}>
                        CSV Generated Successfully!
                    </Text>
                    <Text style={{
                        fontSize: '14px',
                        color: '#666',
                        display: 'block',
                        marginBottom: '20px'
                    }}>
                        Your CSV file has been generated successfully.
                        <br />
                        The download should have started automatically. If not, use the button below.
                    </Text>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleManualDownload}
                        style={{
                            marginTop: '8px'
                        }}
                    >
                        Download CSV File
                    </Button>
                </div>
            );
        }
    };

    const renderFooter = () => {
        if (!isGenerating && !isComplete) {
            return [
                <Button
                    key="cancel"
                    onClick={handleClose}
                    style={{
                        marginRight: '12px'
                    }}
                >
                    Cancel
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    onClick={handleConfirmDownload}
                >
                    Download
                </Button>
            ];
        }

        if (isGenerating) {
            return [
                <Button key="generating" disabled loading>
                    {currentPhase.startsWith('loading:') ? 'Loading Data...' : 'Generating CSV...'}
                </Button>
            ];
        }

        if (isComplete) {
            return [
                <Button key="close" onClick={handleClose}>
                    Close
                </Button>
            ];
        }
    };

    return (
        <MediumModal
            title="Download Variants CSV"
            visible={visible}
            onClose={!isGenerating ? handleClose : () => { }}
            hideFooter={true}
        >
            <div>
                {renderContent()}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '24px',
                    paddingTop: '20px',
                    borderTop: '1px solid #f0f0f0'
                }}>
                    {renderFooter()}
                </div>
            </div>
        </MediumModal>
    );
};

export default DownloadVariantsCSVModal; 